import { Link } from '@tanstack/react-router';
import { useAuth } from '@uturn/portal/modules/auth/hooks';
import {
	Avatar,
	AvatarFallback,
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@uturn/ui-kit';
import { useTranslation } from 'react-i18next';
import ReportABug from './report-a-bug';

/**
 * User navigation dropdown with avatar, profile, and logout
 */
export const UserNav = ({
	dropdownItems,
	user,
	onLogout,
}: {
	dropdownItems?: { name: string; href: string }[];
	user: { name: string; role: string };
	onLogout?: () => void | Promise<void>;
}) => {
	const { t } = useTranslation();
	const { logout } = useAuth();

	const handleLogout = async () => {
		if (onLogout) {
			await onLogout();
		} else {
			await logout();
			window.location.href = '/sign-in';
		}
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" className="relative size-8 rounded-full">
					<Avatar className="rounded-full size-[2.3rem]">
						<AvatarFallback
							name={user.name}
							className="bg-foreground text-primary dark:bg-primary dark:text-primary-foreground"
						/>
					</Avatar>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-56" align="end" forceMount>
				<DropdownMenuLabel className="font-normal">
					<div className="flex flex-col space-y-1">
						<p className="text-sm font-medium leading-none">{user.name}</p>
						<p className="text-muted-foreground text-xs leading-none">
							{user.role}
						</p>
					</div>
				</DropdownMenuLabel>
				{dropdownItems && dropdownItems.length > 0 && (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuGroup>
							{dropdownItems.map((item) => (
								<Link to={item.href} key={item.href}>
									<DropdownMenuItem className="cursor-pointer">
										{item.name}
									</DropdownMenuItem>
								</Link>
							))}
						</DropdownMenuGroup>
					</>
				)}
				<DropdownMenuSeparator />
				<ReportABug />
				<DropdownMenuSeparator />
				<DropdownMenuItem className="cursor-pointer" onClick={handleLogout}>
					{t('header.navigation.user.log-out', 'Log out')}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
